import { trackMeta } from './metaPixel';


const SIGNUP_LABEL = 'AW-18421887250/p5l5CIqo-escEJLintBE';
const PURCHASE_LABEL = 'AW-18421887250/SfXdCI2o-escEJLintBE';

// Enhanced conversions: gtag hashes the email itself. Set from the same user
// object the Meta pixel uses (AuthContext), and passed directly at sign-up
// because the user state effect hasn't run yet at that point.
let userEmail = null;
export const setGoogleAdsUser = (user) => {
  userEmail = (user && user.email && user.email.trim().toLowerCase()) || null;
};
const setUserData = () => {
  if (userEmail) window.gtag('set', 'user_data', { email: userEmail });
};

export const trackGoogleSignup = (user) => {
  if (typeof window.gtag === 'function') {
    if (user) setGoogleAdsUser(user);
    setUserData();
    window.gtag('event', 'conversion', { send_to: SIGNUP_LABEL });
  }
};

export const trackGooglePurchase = (value, currency, transactionId) => {
  if (typeof window.gtag === 'function') {
    setUserData();
    window.gtag('event', 'conversion', {
      send_to: PURCHASE_LABEL,
      value,
      currency,
      transaction_id: transactionId,
    });
  }
};

// Stripe purchases are confirmed against the checkout session itself, not by
// watching credits change: the webhook usually lands before the return page loads.
// Pending session IDs live in localStorage so a failed or unpaid lookup is retried
// on a later load. ponytail: same-browser only; server-side upload covers other devices.
// Meta events for a checkout: a first subscription payment sends Subscribe AND
// Purchase (so Purchases / Purchase ROAS include subscription revenue); a credit
// pack sends Purchase. The event id is the checkout session id, which the Stripe
// webhook also sends through the Conversions API, so Meta counts each once.
// Renewals never reach this page, and coffee donations and $0 checkouts are not sent.
const trackMetaCheckout = (session) => {
  if (!(session.amount_total > 0)) return;
  const kind = session.metadata && session.metadata.kind;
  const events = session.mode === 'subscription' ? ['Subscribe', 'Purchase'] : kind === 'credit_pack' ? ['Purchase'] : [];
  events.forEach((event) =>
    trackMeta(event, {
      value: session.amount_total / 100,
      currency: String(session.currency).toUpperCase(),
      content_name: (session.metadata && (session.metadata.planType || session.metadata.pack)) || undefined,
    }, session.id)
  );
};

const PENDING_KEY = 'gadsPendingPurchases';
const FIRED_KEY = 'gadsFiredPurchases';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const readList = (key) => {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || [];
  } catch (e) {
    return [];
  }
};
const writeList = (key, list) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch (e) {}
};

export const savePendingPurchase = (sessionId) => {
  const pending = readList(PENDING_KEY);
  if (pending.some((p) => p.id === sessionId) || readList(FIRED_KEY).includes(sessionId)) return;
  writeList(PENDING_KEY, [...pending, { id: sessionId, savedAt: Date.now() }]);
};

const runConfirm = async (getSession) => {
  const fired = readList(FIRED_KEY);
  const pending = readList(PENDING_KEY).filter(
    (p) => !fired.includes(p.id) && Date.now() - p.savedAt < MAX_AGE_MS
  );
  const stillPending = [];
  for (const p of pending) {
    try {
      const session = (await getSession(p.id)).data;
      if (session.status === 'complete' && session.payment_status === 'paid') {
        trackGooglePurchase(
          session.amount_total / 100, // minor units; the app bills USD/INR, both 2-decimal
          String(session.currency).toUpperCase(),
          p.id
        );
        trackMetaCheckout(session);
        fired.push(p.id);
        continue;
      }
      // expired, or complete with nothing to pay (100% coupon): nothing to report
      if (session.status === 'expired' || session.payment_status === 'no_payment_required') continue;
    } catch (e) {}
    stillPending.push(p); // lookup failed, or a delayed payment method hasn't settled yet
  }
  writeList(PENDING_KEY, stillPending);
  writeList(FIRED_KEY, fired.slice(-50));
};

// Serialised so the billing page and the app-load retry can't both fire one session.
let queue = Promise.resolve();
export const confirmPendingPurchases = (getSession) => {
  queue = queue.then(() => runConfirm(getSession));
  return queue;
};
